/**
 * Local emoji set for the Chat composer picker and Slack `:shortcode:` rendering.
 * Names follow Slack shortcodes so `:tada:` in a message renders the same glyph.
 */
import { useEffect, useSyncExternalStore } from "react";

export type EmojiEntry = { char: string; name: string; aliases?: string[] };

export type EmojiCategory = { id: string; label: string; icon: string; emoji: EmojiEntry[] };

const e = (char: string, name: string, ...aliases: string[]): EmojiEntry =>
  aliases.length ? { char, name, aliases } : { char, name };

export const EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    id: "smileys",
    label: "Smileys",
    icon: "😀",
    emoji: [
      e("😀", "grinning"),
      e("😃", "smiley"),
      e("😄", "smile"),
      e("😁", "grin"),
      e("😆", "laughing", "satisfied"),
      e("😅", "sweat_smile"),
      e("🤣", "rolling_on_the_floor_laughing", "rofl"),
      e("😂", "joy"),
      e("🙂", "slightly_smiling_face"),
      e("🙃", "upside_down_face"),
      e("😉", "wink"),
      e("😊", "blush"),
      e("😇", "innocent"),
      e("🥰", "smiling_face_with_3_hearts"),
      e("😍", "heart_eyes"),
      e("🤩", "star-struck", "star_struck"),
      e("😘", "kissing_heart"),
      e("😋", "yum"),
      e("😛", "stuck_out_tongue"),
      e("😜", "stuck_out_tongue_winking_eye"),
      e("🤪", "zany_face"),
      e("🤗", "hugging_face", "hugs"),
      e("🤭", "face_with_hand_over_mouth"),
      e("🤫", "shushing_face"),
      e("🤔", "thinking_face", "thinking"),
      e("🤐", "zipper_mouth_face"),
      e("🤨", "face_with_raised_eyebrow"),
      e("😐", "neutral_face"),
      e("😑", "expressionless"),
      e("😶", "no_mouth"),
      e("😏", "smirk"),
      e("😒", "unamused"),
      e("🙄", "face_with_rolling_eyes", "roll_eyes"),
      e("😬", "grimacing"),
      e("😌", "relieved"),
      e("😔", "pensive"),
      e("😪", "sleepy"),
      e("😴", "sleeping"),
      e("😷", "mask"),
      e("🤒", "face_with_thermometer"),
      e("🤯", "exploding_head"),
      e("🥳", "partying_face"),
      e("😎", "sunglasses"),
      e("🤓", "nerd_face"),
      e("🧐", "face_with_monocle"),
      e("😕", "confused"),
      e("😟", "worried"),
      e("🙁", "slightly_frowning_face"),
      e("😮", "open_mouth"),
      e("😲", "astonished"),
      e("😳", "flushed"),
      e("🥺", "pleading_face"),
      e("😢", "cry"),
      e("😭", "sob"),
      e("😱", "scream"),
      e("😤", "triumph"),
      e("😡", "rage"),
      e("😠", "angry"),
      e("🤬", "face_with_symbols_on_mouth"),
      e("😈", "smiling_imp"),
      e("💀", "skull"),
      e("💩", "hankey", "poop"),
      e("🤡", "clown_face"),
      e("👻", "ghost"),
      e("🤖", "robot_face", "robot"),
      e("🫠", "melting_face"),
      e("🫡", "saluting_face"),
    ],
  },
  {
    id: "people",
    label: "Gestures",
    icon: "👍",
    emoji: [
      e("👍", "+1", "thumbsup"),
      e("👎", "-1", "thumbsdown"),
      e("👌", "ok_hand"),
      e("🤌", "pinched_fingers"),
      e("✌️", "v"),
      e("🤞", "crossed_fingers"),
      e("🤟", "i_love_you_hand_sign"),
      e("🤘", "the_horns", "metal"),
      e("🤙", "call_me_hand"),
      e("👈", "point_left"),
      e("👉", "point_right"),
      e("👆", "point_up_2"),
      e("👇", "point_down"),
      e("☝️", "point_up"),
      e("✋", "hand", "raised_hand"),
      e("👋", "wave"),
      e("👏", "clap"),
      e("🙌", "raised_hands"),
      e("👐", "open_hands"),
      e("🤲", "palms_up_together"),
      e("🤝", "handshake"),
      e("🙏", "pray"),
      e("✍️", "writing_hand"),
      e("💪", "muscle"),
      e("🫶", "heart_hands"),
      e("👀", "eyes"),
      e("🧠", "brain"),
      e("🙋", "raising_hand"),
      e("🤷", "shrug"),
      e("🤦", "face_palm", "facepalm"),
      e("🙇", "bow"),
      e("💁", "information_desk_person"),
      e("🧑‍💻", "technologist"),
      e("👨‍💼", "male-office-worker"),
      e("👩‍💼", "female-office-worker"),
    ],
  },
  {
    id: "hearts",
    label: "Hearts",
    icon: "❤️",
    emoji: [
      e("❤️", "heart"),
      e("🧡", "orange_heart"),
      e("💛", "yellow_heart"),
      e("💚", "green_heart"),
      e("💙", "blue_heart"),
      e("💜", "purple_heart"),
      e("🖤", "black_heart"),
      e("🤍", "white_heart"),
      e("🤎", "brown_heart"),
      e("💔", "broken_heart"),
      e("❣️", "heavy_heart_exclamation_mark_ornament"),
      e("💕", "two_hearts"),
      e("💞", "revolving_hearts"),
      e("💓", "heartbeat"),
      e("💗", "heartpulse"),
      e("💖", "sparkling_heart"),
      e("💘", "cupid"),
      e("💝", "gift_heart"),
    ],
  },
  {
    id: "nature",
    label: "Nature",
    icon: "🐶",
    emoji: [
      e("🐶", "dog"),
      e("🐱", "cat"),
      e("🐭", "mouse"),
      e("🦊", "fox_face"),
      e("🐻", "bear"),
      e("🐼", "panda_face"),
      e("🐨", "koala"),
      e("🐯", "tiger"),
      e("🦁", "lion_face"),
      e("🐮", "cow"),
      e("🐷", "pig"),
      e("🐸", "frog"),
      e("🐵", "monkey_face"),
      e("🙈", "see_no_evil"),
      e("🙉", "hear_no_evil"),
      e("🙊", "speak_no_evil"),
      e("🐔", "chicken"),
      e("🐧", "penguin"),
      e("🦉", "owl"),
      e("🦄", "unicorn_face"),
      e("🐝", "bee"),
      e("🦋", "butterfly"),
      e("🐢", "turtle"),
      e("🐙", "octopus"),
      e("🐬", "dolphin"),
      e("🐳", "whale"),
      e("🌵", "cactus"),
      e("🌲", "evergreen_tree"),
      e("🌴", "palm_tree"),
      e("🍀", "four_leaf_clover"),
      e("🌸", "cherry_blossom"),
      e("🌹", "rose"),
      e("🌻", "sunflower"),
      e("🌞", "sun_with_face"),
      e("🌈", "rainbow"),
      e("⭐", "star"),
      e("🌟", "star2"),
      e("✨", "sparkles"),
      e("⚡", "zap"),
      e("🔥", "fire"),
      e("💧", "droplet"),
      e("🌊", "ocean"),
      e("❄️", "snowflake"),
      e("☀️", "sunny"),
      e("☁️", "cloud"),
      e("🌙", "crescent_moon"),
    ],
  },
  {
    id: "food",
    label: "Food",
    icon: "🍕",
    emoji: [
      e("🍎", "apple"),
      e("🍌", "banana"),
      e("🍓", "strawberry"),
      e("🍉", "watermelon"),
      e("🍑", "peach"),
      e("🥑", "avocado"),
      e("🌮", "taco"),
      e("🌯", "burrito"),
      e("🍕", "pizza"),
      e("🍔", "hamburger"),
      e("🍟", "fries"),
      e("🌭", "hotdog"),
      e("🥗", "green_salad"),
      e("🍣", "sushi"),
      e("🍜", "ramen"),
      e("🍩", "doughnut"),
      e("🍪", "cookie"),
      e("🎂", "birthday"),
      e("🧁", "cupcake"),
      e("🍫", "chocolate_bar"),
      e("🍿", "popcorn"),
      e("☕", "coffee"),
      e("🍵", "tea"),
      e("🥤", "cup_with_straw"),
      e("🍺", "beer"),
      e("🍻", "beers"),
      e("🍷", "wine_glass"),
      e("🥂", "clinking_glasses"),
      e("🍾", "champagne"),
    ],
  },
  {
    id: "objects",
    label: "Objects",
    icon: "🎉",
    emoji: [
      e("🎉", "tada"),
      e("🎊", "confetti_ball"),
      e("🎈", "balloon"),
      e("🎁", "gift"),
      e("🏆", "trophy"),
      e("🥇", "first_place_medal"),
      e("🎯", "dart"),
      e("⚽", "soccer"),
      e("🏀", "basketball"),
      e("🏈", "football"),
      e("⚾", "baseball"),
      e("🎾", "tennis"),
      e("⛳", "golf"),
      e("🎮", "video_game"),
      e("🎵", "musical_note"),
      e("🎧", "headphones"),
      e("📣", "mega"),
      e("🔔", "bell"),
      e("💡", "bulb"),
      e("📌", "pushpin"),
      e("📎", "paperclip"),
      e("📝", "memo", "pencil"),
      e("📅", "date"),
      e("📆", "calendar"),
      e("📈", "chart_with_upwards_trend"),
      e("📉", "chart_with_downwards_trend"),
      e("📊", "bar_chart"),
      e("💼", "briefcase"),
      e("📁", "file_folder"),
      e("📧", "email"),
      e("📞", "telephone_receiver"),
      e("💻", "computer"),
      e("🖥️", "desktop_computer"),
      e("📱", "iphone"),
      e("⌨️", "keyboard"),
      e("🔒", "lock"),
      e("🔑", "key"),
      e("🛠️", "hammer_and_wrench"),
      e("⚙️", "gear"),
      e("🧪", "test_tube"),
      e("🚀", "rocket"),
      e("✈️", "airplane"),
      e("🚗", "car"),
      e("🏠", "house"),
      e("🏢", "office"),
      e("⏰", "alarm_clock"),
      e("⏳", "hourglass_flowing_sand"),
      e("💰", "moneybag"),
      e("💵", "dollar"),
      e("💳", "credit_card"),
    ],
  },
  {
    id: "symbols",
    label: "Symbols",
    icon: "✅",
    emoji: [
      e("✅", "white_check_mark"),
      e("☑️", "ballot_box_with_check"),
      e("✔️", "heavy_check_mark"),
      e("❌", "x"),
      e("❎", "negative_squared_cross_mark"),
      e("⚠️", "warning"),
      e("🚫", "no_entry_sign"),
      e("⛔", "no_entry"),
      e("❓", "question"),
      e("❗", "exclamation", "heavy_exclamation_mark"),
      e("‼️", "bangbang"),
      e("💯", "100"),
      e("🆗", "ok"),
      e("🆕", "new"),
      e("🆒", "cool"),
      e("🔴", "red_circle"),
      e("🟠", "large_orange_circle"),
      e("🟡", "large_yellow_circle"),
      e("🟢", "large_green_circle"),
      e("🔵", "large_blue_circle"),
      e("🟣", "large_purple_circle"),
      e("⚪", "white_circle"),
      e("⚫", "black_circle"),
      e("➡️", "arrow_right"),
      e("⬅️", "arrow_left"),
      e("⬆️", "arrow_up"),
      e("⬇️", "arrow_down"),
      e("🔄", "arrows_counterclockwise"),
      e("➕", "heavy_plus_sign"),
      e("➖", "heavy_minus_sign"),
      e("💬", "speech_balloon"),
      e("💭", "thought_balloon"),
      e("🗨️", "left_speech_bubble"),
      e("💤", "zzz"),
      e("🏁", "checkered_flag"),
      e("🇺🇸", "flag-us", "us"),
    ],
  },
];

const SHORTCODES: Map<string, string> = (() => {
  const map = new Map<string, string>();
  for (const category of EMOJI_CATEGORIES) {
    for (const entry of category.emoji) {
      map.set(entry.name, entry.char);
      for (const alias of entry.aliases || []) map.set(alias, entry.char);
    }
  }
  return map;
})();

// Slack's full shortcode set (iamcal, the data Slack itself uses), loaded on demand as its own chunk.
let slackShortcodes: Map<string, string> | null = null;
let slackLoading: Promise<void> | null = null;
let slackVersion = 0;
const slackListeners = new Set<() => void>();

function hexToEmoji(hex: string): string {
  const points = hex.split("-").map((part) => Number.parseInt(part, 16));
  const char = String.fromCodePoint(...points);
  // Lone BMP symbols (❤, ☀) default to text style without VS16.
  return points.length === 1 && points[0]! < 0x1f000 ? `${char}\uFE0F` : char;
}

export function loadSlackEmoji(): Promise<void> {
  if (slackShortcodes) return Promise.resolve();
  if (slackLoading) return slackLoading;
  slackLoading = import("emojibase-data/en/shortcodes/iamcal.json")
    .then((mod) => {
      const data = ((mod as { default?: unknown }).default ?? mod) as Record<string, string | string[]>;
      const map = new Map<string, string>();
      for (const [hex, names] of Object.entries(data)) {
        const char = hexToEmoji(hex);
        for (const name of Array.isArray(names) ? names : [names]) map.set(name.toLowerCase(), char);
      }
      slackShortcodes = map;
      slackVersion += 1;
      for (const listener of slackListeners) listener();
    })
    .catch(() => {
      slackLoading = null;
    });
  return slackLoading;
}

/** Re-render when the full Slack emoji set arrives (returns a version number to key on). */
export function useSlackEmoji(): number {
  const version = useSyncExternalStore(
    (listener) => {
      slackListeners.add(listener);
      return () => slackListeners.delete(listener);
    },
    () => slackVersion,
  );
  useEffect(() => {
    void loadSlackEmoji();
  }, []);
  return version;
}

/** Glyph for a Slack shortcode (without colons), or undefined when not in the local set. */
export function emojiForShortcode(name: string): string | undefined {
  const key = name.toLowerCase();
  return SHORTCODES.get(key) ?? slackShortcodes?.get(key);
}

/** Glyph for a Slack reaction name, including skin tones (`+1::skin-tone-3`). */
export function emojiForReactionName(name: string): string | undefined {
  const [base, tone] = name.replace(/^:|:$/g, "").split("::");
  const glyph = emojiForShortcode(base || "");
  if (!glyph) return undefined;
  const modifier = tone ? emojiForShortcode(tone) : undefined;
  return modifier ? `${glyph.replace(/\uFE0F$/, "")}${modifier.replace(/\uFE0F$/, "")}` : glyph;
}

/** Plain-text previews: `:tada:` and `:+1::skin-tone-3:` become glyphs; unknown codes stay as typed. */
export function replaceShortcodes(text: string): string {
  return text.replace(/:([a-z0-9_+-]+)(?:::(skin-tone-[2-6]))?:/gi, (whole, name: string, tone?: string) => {
    return emojiForReactionName(tone ? `${name}::${tone}` : name) ?? whole;
  });
}

/** Slack shortcode for a glyph (the reverse lookup reactions.add needs). */
export function shortcodeForEmoji(char: string): string | undefined {
  const bare = char.replace(/\uFE0F/g, "");
  for (const category of EMOJI_CATEGORIES) {
    for (const entry of category.emoji) {
      if (entry.char.replace(/\uFE0F/g, "") === bare) return entry.name;
    }
  }
  if (slackShortcodes) {
    for (const [name, glyph] of slackShortcodes) if (glyph.replace(/\uFE0F/g, "") === bare) return name;
  }
  return undefined;
}

export function searchEmoji(query: string, limit = 80): EmojiEntry[] {
  const q = query.trim().toLowerCase().replace(/^:|:$/g, "");
  if (!q) return [];
  const out: EmojiEntry[] = [];
  for (const category of EMOJI_CATEGORIES) {
    for (const entry of category.emoji) {
      const names = [entry.name, ...(entry.aliases || [])];
      if (names.some((n) => n.replace(/[_-]/g, " ").includes(q) || n.includes(q))) {
        out.push(entry);
        if (out.length >= limit) return out;
      }
    }
  }
  return out;
}
